import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import {
  SecurityQuestionsListItem,
  UserInformation,
  VerifyUserRegistration,
  ForgotUserNameInfo,
  ForgotPasswordInfo,
  TemporaryPasswordUserInformation,
} from '../models/registration';

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('RegistrationService');
  }

  public getSecurityQuestionsList(): Observable<SecurityQuestionsListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'registration/securityquestionlist';
    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.getSecurityQuestionsList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<SecurityQuestionsListItem>>(serviceUrl, null).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getSecurityQuestionsList', []))
    );
  }

  public getNewUserInformation(): UserInformation {
    const newUserInformation: UserInformation = {
      ssn: null,
      firstName: null,
      middleInitial: null,
      lastName: null,
      birthDate: null,
      participantId: null,
      userName: null,
      password: null,
      securityQuestionId: null,
      securityQuestionText: null,
      securityQuestionAnswer: null
    };
    return newUserInformation;
  }

  public addUser(userInformation: UserInformation): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'registration/adduser';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.addUser :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<number>(serviceUrl, userInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('addUser', null))
    );
  }

  public getNewVerifyUserRegistration(): VerifyUserRegistration {
    const newVerifyUserRegistration: VerifyUserRegistration = {
      participantId: null,
      registeredFlag: false,
      duplicateUserNameFlag: false
    };
    return newVerifyUserRegistration;
  }

  public verifyRegistration(userInformation: UserInformation): Observable<VerifyUserRegistration> {
    const serviceUrl: string = this._baseServiceUrl + 'registration/verifyuser';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.verifyRegistration :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<VerifyUserRegistration>(serviceUrl, userInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('verifyRegistration', null))
    );
  }

  public getNewForgotUserNameInfo(): ForgotUserNameInfo {
    const newForgotUserNameInfo: ForgotUserNameInfo = {
      ssn: null,
      lastName: null,
      birthDate: null,
      participantId: null,
      companyCode: null,
      homePhone: null,
      cellPhone: null,
      workPhone: null,
      homeEmail: null
    };
    return newForgotUserNameInfo;
  }

  public getForgotUserNameInfo(forgotUserNameInfo: ForgotUserNameInfo): Observable<ForgotUserNameInfo> {
    const serviceUrl: string = this._baseServiceUrl + 'login/getforgotusernameinfo';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.getForgotUserNameInfo :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<ForgotUserNameInfo>(serviceUrl, forgotUserNameInfo).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getForgotUserNameInfo', null))
    );
  }

  public sendForgottenUserName(forgotUserNameInfo: ForgotUserNameInfo): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'login/sendusername';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.sendForgottenUserName :: serviceUrl = ' + serviceUrl);
    }

    // return of 0 equals success
    return this._httpClient.post<number>(serviceUrl, forgotUserNameInfo).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('sendForgottenUserName', null))
    );
  }

  public getNewForgotPasswordInfo(): ForgotPasswordInfo {
    const newForgotPasswordInfo: ForgotPasswordInfo = {
      ssnLastFour: null,
      userName: null,
      participantId: null,
      companyCode: null,
      userId: null,
      homePhone: null,
      cellPhone: null,
      workPhone: null,
      homeEmail: null,
      securityQuestionId: null,
      securityQuestionText: null,
      securityQuestionAnswer: null,
      temporaryPassword: null,
      newPassword: null
    };
    return newForgotPasswordInfo;
  }

  public getForgotPasswordInfo(forgotPasswordInfo: ForgotPasswordInfo): Observable<ForgotPasswordInfo> {
    const serviceUrl: string = this._baseServiceUrl + 'login/getforgotpasswordinfo';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.getForgotPasswordInfo :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<ForgotPasswordInfo>(serviceUrl, forgotPasswordInfo).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getForgotPasswordInfo', null))
    );
  }

  public verifyForgottenPasswordSecurityQuestion(forgotPasswordInfo: ForgotPasswordInfo): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'login/validatesecurityquestion';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.verifyForgottenPasswordSecurityQuestion :: serviceUrl = ' + serviceUrl);
    }

    // return greater than 0 equals success
    return this._httpClient.post<number>(serviceUrl, forgotPasswordInfo).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('verifyForgottenPasswordSecurityQuestion', null))
    );
  }

  public sendTemporaryPassword(forgotPasswordInfo: ForgotPasswordInfo): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'login/sendtemporarypassword';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.setTemporaryPassword :: serviceUrl = ' + serviceUrl);
    }

    // return of 0 equals success
    return this._httpClient.post<number>(serviceUrl, forgotPasswordInfo).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('setTemporaryPassword', null))
    );
  }

  public verifyTemporaryPassword(forgotPasswordInfo: ForgotPasswordInfo): Observable<TemporaryPasswordUserInformation> {
    const serviceUrl: string = this._baseServiceUrl + 'login/checktemporarypassword';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.verifyTemporaryPassword :: serviceUrl = ' + serviceUrl);
    }

    // return greater than 0 equals success
    return this._httpClient.post<TemporaryPasswordUserInformation>(serviceUrl, forgotPasswordInfo).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('verifyTemporaryPassword', null))
    );
  }

  public updatePassword(forgotPasswordInfo: ForgotPasswordInfo): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'login/updatepassword';

    if (this._logging.serviceCallTraceLog) {
      console.log('RegistrationService.updatePassword :: serviceUrl = ' + serviceUrl);
    }

    // return greater than 0 equals success
    return this._httpClient.post<number>(serviceUrl, forgotPasswordInfo).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('updatePassword', null))
    );
  }
}
